import { FC, useState, useEffect } from 'react';
import { User, UserOrganizationRoleStatus } from '@models/user.model';
import { Settings } from '@mui/icons-material';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';

interface ChangeUserStatusModalProps {
  user: User | undefined;
  currentStatus?: UserOrganizationRoleStatus;
  onStatusChange: (status: UserOrganizationRoleStatus) => void;
}

const STATUS_OPTIONS: SelectOption[] = [
  { value: UserOrganizationRoleStatus.ACTIVE, label: 'Active' },
  { value: UserOrganizationRoleStatus.DISABLED, label: 'Disabled' },
  { value: UserOrganizationRoleStatus.READ_ONLY, label: 'Read Only' },
];

const STATUS_DESCRIPTIONS: Record<UserOrganizationRoleStatus, string> = {
  [UserOrganizationRoleStatus.ACTIVE]: 'User has full access to the system',
  [UserOrganizationRoleStatus.DISABLED]: 'User cannot access the system',
  [UserOrganizationRoleStatus.READ_ONLY]: 'User can only view data, cannot make changes',
  [UserOrganizationRoleStatus.OVERDUE]: 'Payment is overdue',
};

const ChangeUserStatusModal: FC<ChangeUserStatusModalProps> = ({
  user,
  currentStatus,
  onStatusChange,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<SelectOption | null>(null);

  useEffect(() => {
    if (currentStatus) {
      const option = STATUS_OPTIONS.find((opt) => opt.value === currentStatus);
      setSelectedStatus(option || null);
    }
  }, [currentStatus]);

  const handleStatusChange = (value: SelectOption | null) => {
    setSelectedStatus(value);
    if (value) {
      onStatusChange(value.value as UserOrganizationRoleStatus);
    }
  };

  return (
    <div className="w-full">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-shrink-0">
          <Settings className="h-8 w-8 text-indigo-500" />
        </div>
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Change the status of this user in your organization
          </p>
        </div>
      </div>

      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
        <p className="text-sm font-medium text-gray-900 dark:text-white">
          {user?.firstName} {user?.lastName}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {user?.email}
        </p>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Status
        </label>
        <ReactSelect
          options={STATUS_OPTIONS}
          value={selectedStatus}
          onChange={(value) => handleStatusChange(value as SelectOption | null)}
          placeholder="Select a status..."
          isClearable={false}
          isSearchable={false}
        />
      </div>

      {selectedStatus && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            {STATUS_DESCRIPTIONS[selectedStatus.value as UserOrganizationRoleStatus]}
          </p>
        </div>
      )}
    </div>
  );
};

export default ChangeUserStatusModal;
